import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useAppDispatch } from 'store'
import { useUserToken } from 'store/account/hooks'
import { useCurrentSymbol } from 'store/market/hooks'
import {
  useBinanceConnectCount,
  useCurrentChartTab,
  useResolution,
} from 'store/orderbook/hooks'
import { TradingViewKLintType } from 'store/orderbook/reducer'
import { useWsConnection, useWsLost } from 'store/ws/hooks'
import { useEffectOnce, useInterval } from 'usehooks-ts'
import {
  initBinanceWebSocket,
  subscribeBinanceKLines,
  subscribeBinancePing,
} from 'ws/binance-ws'
import { zkexConnection } from 'ws/zkex'
import { updateLatencyAction, updatePingTimeAction } from './actions'

export default function Updater() {
  // Zkex ws connection
  useConnectZkexWebSocket()
  useSubscribeOrderbook()
  useSubscribePing()

  // Binance kline ws connection
  useBinanceWs()
  useSubscribeBinanceLine()
  useSubscribeBinancePing()
  useWatchWsLost()

  return null
}

function useConnectZkexWebSocket() {
  const userToken = useUserToken()

  useEffect(() => {
    zkexConnection.connect(userToken)
  }, [userToken])
}

function useSubscribeOrderbook() {
  const { name = '' } = useCurrentSymbol()
  const wsConnection = useWsConnection()

  useEffect(() => {
    if (!name) return
    if (!wsConnection) return
    zkexConnection.subscribe({
      symbol: name,
    })
  }, [name, wsConnection])
}

function useSubscribePing() {
  const wsConnection = useWsConnection()
  const dispatch = useAppDispatch()

  const ping = useCallback(() => {
    zkexConnection.ping()
    dispatch(updatePingTimeAction(new Date().getTime()))
  }, [dispatch])

  useEffect(() => {
    if (wsConnection) ping()
  }, [wsConnection, ping])

  useInterval(ping, wsConnection ? 10000 : null)
}

function useBinanceWs() {
  useEffectOnce(() => {
    initBinanceWebSocket()
  })
}

function useSubscribeBinanceLine() {
  const symbol = useCurrentSymbol()
  const currentChartTab = useCurrentChartTab()
  const resolution = useResolution()
  const connectCount = useBinanceConnectCount()

  useEffect(() => {
    if (!symbol.name) return
    if (currentChartTab !== TradingViewKLintType.BINANCE) return
    const unsubscribeOrderbook = subscribeBinanceKLines(symbol.name, resolution)
    return () => {
      unsubscribeOrderbook()
    }
  }, [symbol.name, currentChartTab, resolution, connectCount])
}

function useSubscribeBinancePing() {
  useEffect(() => {
    const ping = subscribeBinancePing()
    return () => {
      ping()
    }
  }, [])
}

function useWatchWsLost() {
  const lost = useWsLost()
  const dispatch = useDispatch()
  useEffect(() => {
    if (lost > 1) {
      dispatch(updateLatencyAction(0))
    }
  }, [lost])
}
